import { FC, FormEvent, useState } from 'react';

import { useForm, usePage } from '@inertiajs/react';
import { Check, Trash2, X } from 'lucide-react';

import { Button } from '../../../../components/ui/Button';
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '../../../../components/ui/Card';
import { Routes } from '../../../../models/routes';
import {
    CartonProduct,
    Offer,
    OfferActionQuantity,
    OfferActionTypes,
    OfferProperty,
} from '../../../../types';
import { ConfirmModalState } from '../../../../types/ui';
import { FormFields } from '../__shared/FormFields';
import {
    OfferDraftFormValues,
    resolveOfferPropertiesForProduct,
} from '../__shared/offerDraft';
import { withProfileOffersReturnTo } from '../__shared/returnContext';
import { ConfirmModal } from '../Index/ConfirmModal';
import { OfferDraftCard } from '../New/OfferDraftCard';

type PageProps = {
    offerProperties: OfferProperty[];
    initialProductOptions: CartonProduct[];
    offer: Offer;
    returnTo?: string;
};

export const EditOfferForm: FC = () => {
    const { offerProperties, initialProductOptions, offer, returnTo } =
        usePage<PageProps>().props;

    const [selectedProduct, setSelectedProduct] =
        useState<CartonProduct | null>(offer.product ?? null);
    const [removedPhotoIds, setRemovedPhotoIds] = useState<number[]>([]);
    const [photoUploadInProgress, setPhotoUploadInProgress] = useState(false);
    const properties = resolveOfferPropertiesForProduct(
        selectedProduct,
        offerProperties ?? [],
    );

    const { data, setData, patch, processing, errors, transform } =
        useForm<OfferDraftFormValues>({
            product_id: offer.product.id.toString(),
            price: offer.price.toString(),
            amount: offer.amount.toString(),
            property_set: offer.property_set ?? {},
            photos: [],
        });
    const remainingPhotosCount =
        offer.photos.filter((photo) => !removedPhotoIds.includes(photo.id))
            .length + data.photos.length;
    const isSubmitDisabled =
        !selectedProduct ||
        !data.price ||
        !data.amount ||
        data.price.trim() === '' ||
        data.amount.trim() === '' ||
        photoUploadInProgress ||
        (selectedProduct.offer_photos_required && remainingPhotosCount === 0);

    const [confirmModalState, setConfirmModalState] =
        useState<ConfirmModalState>({
            open: false,
            payload: null,
        });

    function submit(e: FormEvent) {
        e.preventDefault();
        transform(({ photos, ...values }) => ({
            offer: {
                ...values,
                new_photo_blob_ids: photos.map((photo) => photo.signed_id),
                removed_photo_ids: removedPhotoIds,
            },
        }));
        patch(
            withProfileOffersReturnTo(`${Routes.Offers}/${offer.id}`, returnTo),
        );
    }

    return (
        <form onSubmit={submit} className="grid grid-cols-1 gap-6 sm:gap-8">
            {confirmModalState.open && (
                <ConfirmModal
                    state={confirmModalState}
                    setState={setConfirmModalState}
                />
            )}
            <Card className="border-0 bg-transparent shadow-none sm:border sm:bg-card sm:shadow-sm">
                <CardHeader className="px-0 pt-0 sm:px-6 sm:pt-6">
                    <CardTitle className="text-base sm:text-lg">
                        Редагувати пропозицію
                    </CardTitle>
                    <CardDescription>
                        Оновіть деталі вашої пропозиції
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4 px-0 pt-0 sm:px-6 sm:pt-0">
                    <FormFields
                        selectedProduct={selectedProduct}
                        offerProperties={properties}
                        amount={data.amount}
                        price={data.price}
                        propertySet={data.property_set}
                        photos={data.photos}
                        existingPhotos={offer.photos}
                        removedPhotoIds={removedPhotoIds}
                        onRemoveExistingPhoto={(photoId) =>
                            setRemovedPhotoIds((currentIds) => [
                                ...currentIds,
                                photoId,
                            ])
                        }
                        onPhotoUploadingChange={setPhotoUploadInProgress}
                        initialProductOptions={initialProductOptions}
                        setData={setData}
                        setSelectedProduct={setSelectedProduct}
                        errors={errors}
                        isEditing={true}
                        embedded
                    />

                    {selectedProduct && (
                        <div className="space-y-3">
                            <div
                                aria-hidden="true"
                                className="h-px w-full bg-border"
                            />
                            <p className="text-sm font-medium">
                                Інформація про пропозицію
                            </p>
                            <OfferDraftCard
                                product={selectedProduct}
                                properties={properties}
                                propertySet={data.property_set}
                                amount={data.amount}
                                price={data.price}
                                photos={data.photos}
                            />
                        </div>
                    )}

                    <div className="flex flex-col gap-3 sm:flex-row">
                        <Button
                            type="submit"
                            variant="success"
                            className="sm:flex-1"
                            disabled={processing || isSubmitDisabled}
                        >
                            Зберегти пропозицію
                        </Button>
                    </div>

                    <div className="space-y-2 border-t pt-4">
                        <div className="flex gap-2">
                            {offer.status === 'active' ? (
                                <Button
                                    type="button"
                                    variant="outline"
                                    className="h-9 flex-1 text-xs sm:text-sm"
                                    onClick={() => {
                                        setConfirmModalState({
                                            open: true,
                                            payload: {
                                                type: OfferActionTypes.Deactivate,
                                                quantity:
                                                    OfferActionQuantity.Single,
                                                offerId: offer.id,
                                            },
                                        });
                                    }}
                                    disabled={processing}
                                >
                                    <X className="mr-1 h-4 w-4" />
                                    Деактивувати
                                </Button>
                            ) : (
                                <Button
                                    type="button"
                                    variant="outline"
                                    className="h-9 flex-1 text-xs sm:text-sm"
                                    onClick={() => {
                                        setConfirmModalState({
                                            open: true,
                                            payload: {
                                                type: OfferActionTypes.Activate,
                                                quantity:
                                                    OfferActionQuantity.Single,
                                                offerId: offer.id,
                                            },
                                        });
                                    }}
                                    disabled={processing}
                                >
                                    <Check className="mr-1 h-4 w-4" />
                                    Активувати
                                </Button>
                            )}
                        </div>
                        <Button
                            type="button"
                            variant="destructive"
                            className="h-9 w-full text-xs sm:text-sm"
                            onClick={() => {
                                setConfirmModalState({
                                    open: true,
                                    payload: {
                                        type: OfferActionTypes.Delete,
                                        quantity: OfferActionQuantity.Single,
                                        offerId: offer.id,
                                    },
                                });
                            }}
                            disabled={processing}
                        >
                            <Trash2 className="mr-1 h-4 w-4" />
                            Видалити пропозицію
                        </Button>
                    </div>
                </CardContent>
            </Card>
        </form>
    );
};
